import { ArrowLeft, FileUp, LoaderCircle, Save } from "lucide-react";
import { type ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { createDxfFloorReference } from "../cad/dxf-floor-reference";
import CargoSceneEditor from "../components/cargo-scene-editor";
import type { DeviceModelDefinition } from "../models/device-model-catalog";
import {
  getProject,
  loadProjectDrawing,
  removeProjectDrawing,
  renameProject,
  replaceProjectDrawing,
  saveProjectSceneState
} from "./project-api";
import type { ProjectDetail, ProjectSceneState } from "./project-types";

type SaveStatus = "error" | "idle" | "saved" | "saving";

type ProjectWorkspaceProps = {
  deviceModels: DeviceModelDefinition[];
  onBack: () => void;
  onManageDevices: () => void;
  projectId: string;
};

export function ProjectWorkspace({
  deviceModels,
  onBack,
  onManageDevices,
  projectId
}: ProjectWorkspaceProps) {
  const [busy, setBusy] = useState(false);
  const [drawingFile, setDrawingFile] = useState<File>();
  const [error, setError] = useState<string>();
  const [nameDraft, setNameDraft] = useState("");
  const [project, setProject] = useState<ProjectDetail>();
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [status, setStatus] = useState<"error" | "loading" | "ready">("loading");
  const projectRef = useRef<ProjectDetail | undefined>(undefined);
  const saveTimerRef = useRef<number | undefined>(undefined);
  const pendingStateRef = useRef<ProjectSceneState | undefined>(undefined);
  const saveInFlightRef = useRef(false);
  const saveEpochRef = useRef(0);
  const flushOnSettleRef = useRef(false);
  const replacingRef = useRef(false);

  const load = useCallback(async () => {
    setStatus("loading");
    setError(undefined);
    try {
      const detail = await getProject(projectId);
      const file = await loadProjectDrawing(detail);
      projectRef.current = detail;
      setProject(detail);
      setNameDraft(detail.name);
      setDrawingFile(file);
      setSaveStatus("saved");
      setStatus("ready");
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "读取项目失败");
      setStatus("error");
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const flushSave = useCallback(async () => {
    if (saveInFlightRef.current || replacingRef.current) {
      return;
    }
    const currentProject = projectRef.current;
    const state = pendingStateRef.current;
    if (!currentProject || !state) {
      return;
    }
    pendingStateRef.current = undefined;
    const saveEpoch = saveEpochRef.current;
    saveInFlightRef.current = true;
    let shouldContinue = false;
    setSaveStatus("saving");
    try {
      const result = await saveProjectSceneState(
        currentProject.id,
        currentProject.drawingGeneration,
        currentProject.sceneStateVersion,
        state
      );
      if (saveEpoch !== saveEpochRef.current) {
        return;
      }
      const latestProject = projectRef.current;
      if (!latestProject || latestProject.drawingGeneration !== currentProject.drawingGeneration) {
        return;
      }
      const updated = {
        ...latestProject,
        sceneState: state,
        sceneStateVersion: result.version,
        updatedAt:
          latestProject.updatedAt > result.updatedAt ? latestProject.updatedAt : result.updatedAt
      };
      projectRef.current = updated;
      setProject(updated);
      setError(undefined);
      setSaveStatus("saved");
      shouldContinue = Boolean(pendingStateRef.current);
    } catch (saveError) {
      if (saveEpoch !== saveEpochRef.current) {
        return;
      }
      const newerState = pendingStateRef.current;
      pendingStateRef.current = newerState ?? state;
      shouldContinue = Boolean(newerState) || flushOnSettleRef.current;
      setError(saveError instanceof Error ? saveError.message : "保存场景设置失败");
      setSaveStatus("error");
    } finally {
      saveInFlightRef.current = false;
      if (saveEpoch === saveEpochRef.current && shouldContinue && !replacingRef.current) {
        if (flushOnSettleRef.current) {
          flushOnSettleRef.current = false;
          void flushSave();
        } else {
          saveTimerRef.current = window.setTimeout(() => void flushSave(), 600);
        }
      }
    }
  }, []);

  useEffect(
    () => () => {
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current);
      }
      if (replacingRef.current) {
        return;
      }
      if (saveInFlightRef.current) {
        flushOnSettleRef.current = true;
      } else if (pendingStateRef.current) {
        void flushSave();
      }
    },
    [flushSave]
  );

  const handleSceneStateChange = useCallback(
    (state: ProjectSceneState) => {
      if (replacingRef.current) {
        return;
      }
      pendingStateRef.current = state;
      setSaveStatus("idle");
      setError(undefined);
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current);
      }
      saveTimerRef.current = window.setTimeout(() => void flushSave(), 600);
    },
    [flushSave]
  );

  const handleReplace = async (file: File) => {
    const currentProject = projectRef.current;
    if (!currentProject || replacingRef.current) {
      return;
    }
    if (
      currentProject.drawingFileName &&
      !window.confirm("替换图纸会清空全部货架、货物、设备、渲染选项和已保存视角。确定继续吗？")
    ) {
      return;
    }

    replacingRef.current = true;
    saveEpochRef.current += 1;
    pendingStateRef.current = undefined;
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
    }
    setBusy(true);
    setError(undefined);
    try {
      await validateDxf(file, deviceModels);
      const updated = await replaceProjectDrawing(currentProject, file);
      projectRef.current = updated;
      setProject(updated);
      setDrawingFile(file);
      setSaveStatus("saved");
    } catch (replaceError) {
      setError(replaceError instanceof Error ? replaceError.message : "替换图纸失败");
      throw replaceError;
    } finally {
      replacingRef.current = false;
      setBusy(false);
    }
  };

  const handleRemoveDrawing = async () => {
    const currentProject = projectRef.current;
    if (!currentProject?.drawingFileName || replacingRef.current) {
      return;
    }
    if (!window.confirm("确定删除当前图纸吗？全部场景设置也会被清空。")) {
      return;
    }
    replacingRef.current = true;
    saveEpochRef.current += 1;
    pendingStateRef.current = undefined;
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
    }
    setBusy(true);
    setError(undefined);
    try {
      await removeProjectDrawing(currentProject);
      const updated = await getProject(currentProject.id);
      projectRef.current = updated;
      setProject(updated);
      setDrawingFile(undefined);
      setSaveStatus("saved");
    } catch (removeError) {
      setError(removeError instanceof Error ? removeError.message : "删除图纸失败");
    } finally {
      replacingRef.current = false;
      setBusy(false);
    }
  };

  const handleRename = async () => {
    const currentProject = projectRef.current;
    if (!currentProject || !nameDraft.trim() || nameDraft.trim() === currentProject.name) {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const summary = await renameProject(currentProject.id, nameDraft, currentProject.version);
      const latestProject = projectRef.current;
      if (!latestProject || latestProject.id !== currentProject.id) {
        return;
      }
      const updated = { ...latestProject, ...summary };
      projectRef.current = updated;
      setProject(updated);
      setNameDraft(updated.name);
    } catch (renameError) {
      setError(renameError instanceof Error ? renameError.message : "修改项目名称失败");
    } finally {
      setBusy(false);
    }
  };

  if (status === "loading") {
    return (
      <div className="project-workspace-gate">
        <LoaderCircle aria-hidden="true" size={24} />
        正在读取项目...
      </div>
    );
  }
  if (status === "error" || !project) {
    return (
      <div className="project-workspace-gate is-error">
        <strong>项目加载失败</strong>
        <span>{error}</span>
        <button onClick={() => void load()} type="button">
          重试
        </button>
      </div>
    );
  }

  return (
    <main className="project-workspace">
      <div className="project-toolbar">
        <button aria-label="返回项目列表" onClick={onBack} title="返回项目列表" type="button">
          <ArrowLeft aria-hidden="true" size={18} />
        </button>
        <input
          aria-label="项目名称"
          disabled={busy}
          maxLength={80}
          onBlur={() => void handleRename()}
          onChange={(event) => setNameDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.currentTarget.blur();
            }
          }}
          value={nameDraft}
        />
        <span className={`project-save-state is-${saveStatus}`}>
          {saveStatus === "saving" ? (
            <LoaderCircle aria-hidden="true" size={14} />
          ) : (
            <Save aria-hidden="true" size={14} />
          )}
          {saveStatus === "saving"
            ? "保存中"
            : saveStatus === "error"
              ? "保存失败"
              : saveStatus === "idle"
                ? "等待保存"
                : "已保存"}
        </span>
      </div>

      {drawingFile ? (
        <CargoSceneEditor
          deviceModels={deviceModels}
          drawingFile={drawingFile}
          initialSceneState={project.sceneState}
          key={`${project.id}-${project.drawingGeneration}`}
          onManageDevices={onManageDevices}
          onRemoveDrawing={handleRemoveDrawing}
          onReplaceDrawing={handleReplace}
          onSceneStateChange={handleSceneStateChange}
        />
      ) : (
        <EmptyProjectDrawing
          busy={busy}
          error={error}
          onFile={handleReplace}
          projectName={project.name}
        />
      )}
      {drawingFile && error ? <div className="project-workspace-error">{error}</div> : null}
    </main>
  );
}

function EmptyProjectDrawing({
  busy,
  error,
  onFile,
  projectName
}: {
  busy: boolean;
  error?: string;
  onFile: (file: File) => Promise<void>;
  projectName: string;
}) {
  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      void onFile(file).catch(() => undefined);
    }
    event.target.value = "";
  };
  return (
    <section className="project-empty-drawing">
      <FileUp aria-hidden="true" size={30} />
      <div>
        <span>{projectName}</span>
        <h1>上传项目图纸</h1>
        <p>选择 DXF 后，系统会先在本地完成识别，再压缩上传并创建场景。</p>
      </div>
      <label className={busy ? "is-disabled" : ""}>
        {busy ? (
          <LoaderCircle aria-hidden="true" size={18} />
        ) : (
          <FileUp aria-hidden="true" size={18} />
        )}
        {busy ? "正在处理图纸" : "选择 DXF 文件"}
        <input accept=".dxf" disabled={busy} onChange={handleFile} type="file" />
      </label>
      {error ? <p className="project-list-error">{error}</p> : null}
    </section>
  );
}

async function validateDxf(file: File, models: readonly DeviceModelDefinition[]) {
  if (!file.name.toLowerCase().endsWith(".dxf")) {
    throw new Error("请选择 .dxf 文件");
  }
  const reference = await createDxfFloorReference(file, {
    anisotropy: 1,
    deviceBlockPrefixes: [...new Set(models.map((model) => model.blockPrefix))],
    maxTextureSize: 2048
  });
  reference.dispose();
}
